import {
  SelectOptionOld,
  useWSSnackbar,
  WSActions,
  WSDivider,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import {
  QUICKBOOKS_ENTITY,
  RefreshModal,
  useQboRefreshModal
} from "../../../../Settings/screens/Integrations/quickbooks/RefreshButton";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useIntegrationsQuickbooks,
  useIntegrationsQuickbooksAccountExpenses,
  useIntegrationsQuickbooksVendors
} from "../../../../query/integrations/queries";
import { useCreatePayeeEngagement } from "../../../../query/payeeEngagements/mutations/useCreatePayeeEngagement";
import { selectQuickbooksAccountOrItemOptions } from "../../../Integrations/selectors/selectQuickbooksAccountOrItemOptions";
import { selectQuickbooksUserOptions } from "../../../Integrations/selectors/selectQuickbooksUserOptions";
import { selectorIsQBOActive } from "../../../../shared/selectors/selectorIsQBOActive";
import { useEngagementsListAllQuery } from "../../../../query/engagements/queries/useEngagementsListAllQuery";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useQueryPayee } from "../../../../query/payee/queries/useQueryPayee";
import { getPayeeNames } from "../../selectors/getPayeeNames";
import { usePayeeEngagementsListByPayeeIdQuery } from "../../../../query/payeeEngagements/queries/usePayeeEngagementsListByPayeeIdQuery";
import { FormPartialPayeeSearchField } from "../../../../shared/components/PayeeSearchField";
import { useEngagementQuery } from "../../../../query/engagements/queries/useEngagementQuery";

export type CreateEngagementModalProps = {
  onClose(result: boolean): void;
  payeeId?: string;
  engagementId?: string;
};

export const CreateEngagementModal: React.FC<CreateEngagementModalProps> = ({
  payeeId,
  engagementId,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const queryFeatureFlags = useFeatureFlags();
  const qboRefreshModal = useQboRefreshModal();

  const queryEngagementsTypes = useEngagementsListAllQuery(
    { payeeId },
    {
      enabled: !!payeeId
    }
  );

  const queryPayeeEngagements = usePayeeEngagementsListByPayeeIdQuery(
    payeeId!,
    {},
    {
      enabled: !!payeeId
    }
  );

  const queryIntegrationsQuickbooks = useIntegrationsQuickbooks();
  const queryIntegrationsQuickbooksVendors = useIntegrationsQuickbooksVendors();
  const queryIntegrationsQuickbooksExpenseAccounts =
    useIntegrationsQuickbooksAccountExpenses();

  const queryPayee = useQueryPayee(payeeId!, {
    enabled: !!payeeId
  });

  const queryEngagement = useEngagementQuery(engagementId!, {
    enabled: !!engagementId
  });

  const isQuickbooksActive = selectorIsQBOActive(
    queryFeatureFlags?.data,
    queryIntegrationsQuickbooks.data
  );

  const [createEngagement, createEngagementMeta] = useCreatePayeeEngagement();

  const quickbooksVendorOptions: SelectOptionOld[] =
    selectQuickbooksUserOptions(queryIntegrationsQuickbooksVendors.data);

  const quickbooksAccountsOptions: SelectOptionOld[] =
    selectQuickbooksAccountOrItemOptions(
      queryIntegrationsQuickbooksExpenseAccounts.data
    );

  const defaultAccount = (
    queryIntegrationsQuickbooksExpenseAccounts.data ?? []
  ).find(
    acct =>
      acct.accountId ===
      queryIntegrationsQuickbooks.data?.defaults?.expenseAccountId
  );

  const engagementsOptions =
    queryEngagementsTypes.data
      ?.filter(
        engagement =>
          !engagement.isDefault &&
          !queryPayeeEngagements.data?.some(
            eng => eng.engagementId === engagement.engagementId
          )
      )
      .map(engagement => ({
        label: engagement.name,
        value: engagement.engagementId
      })) || [];

  const payeeName = queryPayee.data
    ? getPayeeNames(queryPayee.data).getResolvedName()
    : "";

  return (
    <WSElement
      shimmer={
        payeeId
          ? queryPayee.isLoading ||
            queryEngagementsTypes.isLoading ||
            queryPayeeEngagements.isLoading
          : queryEngagement.isLoading
      }
    >
      <WSForm
        defaultValues={{
          qboVendorId: "",
          qboExpenseAccountId: "",
          engagementId: engagementId || "",
          payeeId: payeeId || ""
        }}
        validationSchema={Yup.object().shape({
          payeeId: Yup.string().required("Contractor is required!"),
          engagementId: Yup.string().required("Engagement is required")
        })}
        onSubmit={async data => {
          const result = await createEngagement({
            payeeId: data.payeeId,
            payerOwnedData: {
              integration: {
                quickbooks: {
                  vendorId: data.qboVendorId,
                  expenseAccountId: data.qboExpenseAccountId
                }
              }
            },
            engagementId: data.engagementId
          });

          if (result) {
            onClose(true);
            openSnackbar({
              message: "Engagement created successfully",
              type: "success"
            });
          }
        }}
      >
        <WSList gap="XL">
          {payeeId ? (
            <WSForm.Field
              name="payeeId"
              label="Contractor"
              component={WSSelect}
              componentProps={{
                disabled: true,
                options: [{ value: payeeId || "", label: payeeName }]
              }}
            />
          ) : (
            <FormPartialPayeeSearchField
              name="payeeId"
              engagementId={engagementId!}
            />
          )}
          {engagementId ? (
            <WSForm.Field
              name="engagementId"
              label="Engagement type"
              component={WSSelect}
              componentProps={{
                disabled: true,
                options: [
                  {
                    value: engagementId,
                    label:
                      queryEngagement.data?.name || "Current engagement type"
                  }
                ]
              }}
            />
          ) : engagementsOptions.length > 0 ? (
            <WSForm.Field
              name="engagementId"
              label="Engagement type"
              component={WSSelect}
              componentProps={{
                required: true,
                options: engagementsOptions
              }}
            />
          ) : (
            <WSText.ParagraphSm align="center">
              No engagement types available
            </WSText.ParagraphSm>
          )}
          {isQuickbooksActive &&
            quickbooksVendorOptions.length > 0 &&
            quickbooksAccountsOptions.length > 0 && (
              <WSList gap="L">
                <WSDivider label="Quickbooks Mapping" />
                <WSInfoBox>
                  Because you have an active integration with QBO, please map
                  this new Contractor to an existing Vendor, or leave blank and
                  we will create a new one. You can also set a default expense
                  account for payable line items.
                </WSInfoBox>
                <WSForm.Field
                  name="qboVendorId"
                  component={WSSelect}
                  componentProps={{
                    placeholder: "Not mapped, create new Vendor",
                    options: quickbooksVendorOptions,
                    menuFooterAction: {
                      label: "Resync QBO Vendors",
                      icon: "refresh-v",
                      onClick: () => {
                        qboRefreshModal.open({
                          entity: QUICKBOOKS_ENTITY.VENDORS
                        });
                      }
                    }
                  }}
                  label="QBO Vendor"
                />
                <WSForm.Field
                  name="qboExpenseAccountId"
                  component={WSSelect}
                  componentProps={{
                    options: quickbooksAccountsOptions,
                    placeholder: `Use default "${defaultAccount?.fullyQualifiedName}"`,
                    menuFooterAction: {
                      label: "Resync QBO Expenses Accounts",
                      icon: "refresh-v",
                      onClick: () => {
                        qboRefreshModal.open({
                          entity: QUICKBOOKS_ENTITY.EXPENSES
                        });
                      }
                    }
                  }}
                  label="Default QBO Expense Account"
                />
                <RefreshModal />
              </WSList>
            )}

          <WSInfoBox title="Adding contractor to engagement">
            Contractor added to an engagement will be required to complete
            engagement-specific requirements (if any) in addition to general
            requirements in order to receive payments for that engagement. You
            can archive or restore a contractor’s engagement at any time.
          </WSInfoBox>
        </WSList>

        <WSErrorMessage
          mt="L"
          contextKey="Engagement"
          error={createEngagementMeta.error}
        />

        <WSActions
          mt="3XL"
          alignment="fill"
          buttons={[
            {
              label: "Add engagement",
              kind: "Primary",
              type: "submit",
              loading: createEngagementMeta.isLoading
            },
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick() {
                onClose(false);
              }
            }
          ]}
        />
      </WSForm>
    </WSElement>
  );
};
